import { createFileRoute, Link } from "@tanstack/react-router";

export const Route = createFileRoute("/docs_/integrations")({
  component: IntegrationsDocsPage,
});

function IntegrationsDocsPage() {
  return (
    <div className="max-w-4xl mx-auto bg-[#F5F5F5] -m-6 p-8 min-h-screen">
        {/* Breadcrumb */}
        <nav className="mb-8">
          <ol className="flex items-center gap-2 text-sm" style={{ color: "#A9A9A9" }}>
            <li>
              <Link to="/docs" className="hover:text-[#171717] transition-colors">
                Documentation
              </Link>
            </li>
            <li>/</li>
            <li style={{ color: "#171717" }}>Integrations</li>
          </ol>
        </nav>

        <h1 className="text-4xl mb-6" style={{ color: "#171717", fontWeight: 300, lineHeight: 1.1 }}>Integrations</h1>

        <div className="space-y-8">
          <p className="text-lg mb-8" style={{ color: "#171717", fontWeight: 300, lineHeight: 1.4 }}>
            Connect your existing systems to the Product Intelligence Cloud.
            We support popular e-commerce platforms, PIM systems, and custom
            integrations via API.
          </p>

          {/* Shopify */}
          <section className="mb-12">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-12  flex items-center justify-center" style={{ backgroundColor: "#5BC48B" }}>
                <svg className="w-6 h-6" viewBox="0 0 24 24" fill="#FFFFFF">
                  <path d="M15.337 3.652c-.035-.132-.165-.2-.287-.212-.123-.012-2.487-.184-2.487-.184s-1.644-1.644-1.836-1.836c-.192-.192-.564-.135-.708-.095-.02.005-.354.11-.906.281-.54-1.556-1.491-2.984-3.166-2.984-.046 0-.094.002-.14.004C5.312.412 4.6-.017 3.772-.017 1.69-.017.07 2.5.07 5.702c0 .827.168 1.59.409 2.291H.4s-.368.11-.42.41c-.05.29-1.19 9.218-1.19 9.218s-.114.774.425 1.02c.54.245 9.155 1.954 9.155 1.954s.557.13.82-.013c.262-.144.41-.385.41-.385l4.11-2.59c.218-.137.368-.362.405-.613.037-.25-.034-.505-.192-.692l-3.387-4.006s-.14-.165-.353-.165c-.113 0-.24.046-.36.166l-2.222 2.093-1.664-4.153 6.93-1.652s.3-.073.407-.295c.107-.222-.007-.482-.157-.594z" />
                </svg>
              </div>
              <h2 className="text-2xl m-0" style={{ color: "#171717", fontWeight: 300 }}>Shopify</h2>
            </div>

            <div className=" p-6" style={{ backgroundColor: "#FFFFFF" }}>
              <p className="mb-4" style={{ color: "#171717", fontWeight: 300 }}>
                Sync your Shopify product catalogue directly to the Product
                Intelligence Cloud. All product data including variants,
                images, and descriptions are imported automatically.
              </p>

              <h4 className="text-lg mb-3" style={{ color: "#171717", fontWeight: 300 }}>Setup Steps</h4>
              <ol className="list-decimal list-inside space-y-2 mb-4" style={{ color: "#171717", fontWeight: 300 }}>
                <li>Create a private app in your Shopify admin</li>
                <li>Grant read access to Products</li>
                <li>Copy your Admin API access token</li>
                <li>Navigate to Products → Sync Shopify</li>
                <li>Enter your store URL and access token</li>
                <li>Click "Sync Now" to import products</li>
              </ol>

              <div className=" p-4" style={{ backgroundColor: "#171717" }}>
                <p className="text-xs mb-2 uppercase tracking-wide" style={{ color: "#A9A9A9", fontFamily: "'Atkinson Hyperlegible Mono', monospace" }}>Required Shopify API scopes</p>
                <code style={{ color: "#00FBA9", fontFamily: "'Atkinson Hyperlegible Mono', monospace" }}>read_products, read_product_listings</code>
              </div>
            </div>
          </section>

          {/* CSV Upload */}
          <section className="mb-12">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-12  flex items-center justify-center" style={{ backgroundColor: "#3941EB" }}>
                <span className="text-white text-xl">CSV</span>
              </div>
              <h2 className="text-2xl m-0" style={{ color: "#171717", fontWeight: 300 }}>CSV Upload</h2>
            </div>

            <div className=" p-6" style={{ backgroundColor: "#FFFFFF" }}>
              <p className="mb-4" style={{ color: "#171717", fontWeight: 300 }}>
                Upload product data from any source using our CSV import
                wizard. The wizard automatically detects columns and allows
                manual mapping.
              </p>

              <h4 className="text-lg mb-3" style={{ color: "#171717", fontWeight: 300 }}>Supported Format</h4>
              <div className=" p-4 text-sm overflow-x-auto mb-4" style={{ backgroundColor: "#171717" }}>
                <pre style={{ color: "#FAFAFA", fontFamily: "'Atkinson Hyperlegible Mono', monospace" }}>
                  title,sku,brand,category,price,description,imageUrl{"\n"}
                  "Gucci Marmont Bag",GUC-001,Gucci,Handbags,2300,"Leather handbag","https://..."{"\n"}
                  "Louis Vuitton Neverfull",LV-042,Louis Vuitton,Bags,1500,"Classic tote","https://..."
                </pre>
              </div>

              <Link to="/products" search={{ import: true }} className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm uppercase tracking-wide transition-colors" style={{ backgroundColor: "#00FBA9", color: "#171717", fontWeight: 300 }}>
                Import CSV
              </Link>
            </div>
          </section>

          {/* REST API */}
          <section className="mb-12">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-12  flex items-center justify-center" style={{ backgroundColor: "#FF4C00" }}>
                <span className="text-white text-xl">API</span>
              </div>
              <h2 className="text-2xl m-0" style={{ color: "#171717", fontWeight: 300 }}>REST API</h2>
            </div>

            <div className=" p-6" style={{ backgroundColor: "#FFFFFF" }}>
              <p className="mb-4" style={{ color: "#171717", fontWeight: 300 }}>
                Build custom integrations using our REST API. Perfect for PIM
                systems, ERPs, and custom data pipelines.
              </p>

              <h4 className="text-lg mb-3" style={{ color: "#171717", fontWeight: 300 }}>Create Product via API</h4>
              <div className=" p-4 text-sm overflow-x-auto mb-4" style={{ backgroundColor: "#171717" }}>
                <pre style={{ color: "#FAFAFA", fontFamily: "'Atkinson Hyperlegible Mono', monospace" }}>
                  <span style={{ color: "#00FBA9" }}>POST</span> /api/products{"\n"}
                  <span style={{ color: "#A9A9A9" }}>Authorization: Bearer pic_your_key</span>{"\n"}
                  <span style={{ color: "#A9A9A9" }}>Content-Type: application/json</span>{"\n\n"}
                  {JSON.stringify({ title: "Product Name", sku: "SKU-001", brand: "Brand Name", category: "Category", price: 99.99, description: "Product description", image_url: "https://example.com/image.jpg" }, null, 2)}
                </pre>
              </div>

              <Link to="/docs" style={{ color: "#3941EB", fontWeight: 300 }}>
                View full API documentation →
              </Link>
            </div>
          </section>

          {/* Coming Soon */}
          <section className="mb-12">
            <h2 className="text-2xl mb-6" style={{ color: "#171717", fontWeight: 300 }}>Coming Soon</h2>

            <div className="grid gap-4 md:grid-cols-2">
              {[
                { name: "WooCommerce", desc: "WordPress WooCommerce integration", date: "Q2 2026" },
                { name: "Akeneo PIM", desc: "Bi-directional PIM synchronization", date: "Q2 2026" },
                { name: "BigCommerce", desc: "BigCommerce product sync", date: "Q3 2026" },
                { name: "Webhooks", desc: "Real-time event notifications", date: "Q2 2026" },
              ].map(({ name, desc, date }) => (
                <div key={name} className=" p-5 border opacity-60" style={{ backgroundColor: "#FFFFFF", borderColor: "#E5E5E5" }}>
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg" style={{ color: "#171717", fontWeight: 300 }}>{name}</h3>
                    <span className="ml-auto text-xs px-2 py-1 rounded-full uppercase" style={{ backgroundColor: "#FAFAFA", color: "#A9A9A9", fontFamily: "'Atkinson Hyperlegible Mono', monospace" }}>{date}</span>
                  </div>
                  <p className="text-sm" style={{ color: "#A9A9A9", fontWeight: 300 }}>{desc}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Navigation */}
          <div className="flex justify-between pt-6" style={{ borderTop: "1px solid #E5E5E5" }}>
            <Link to="/docs/products" style={{ color: "#3941EB", fontWeight: 300 }}>← Products</Link>
            <Link to="/docs" style={{ color: "#3941EB", fontWeight: 300 }}>API Reference →</Link>
          </div>
        </div>
      </div>
  );
}

